import { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Fab,
  Avatar,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useDispatch } from "react-redux";
import { useTheme } from "@emotion/react";
import { API_BASE_URL } from "../../../../config";
import { useFormRCAutoDetails } from "../rcautoContext";

export default function ChatComponent({ idUID, fetchMessages }) {
  const { formState, dispatch: dispatchForm } = useFormRCAutoDetails();
  const [input, setInput] = useState("");
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const theme = useTheme();

  const MAX_MESSAGE_LENGTH = 200;

  const loggedInUserId = sessionStorage.getItem("uid");
  const nome = sessionStorage.getItem("nome");
  const cognome = sessionStorage.getItem("cognome");

  const handleSend = async () => {
    if (!input.trim() || input.length > MAX_MESSAGE_LENGTH) return;

    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    const formattedDate = `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

    const newMessage = {
      id: Date.now(),
      messaggio: input.trim(),
      cognome: cognome || "", // Could be fetched from session if available
      nome: nome || "Me",
      idUIDUserId: loggedInUserId,
      dataMessaggio: formattedDate,
      nomeAvatar: (nome || "M").substring(0, 2).toUpperCase(),
    };

    // Update global state
    dispatchForm({
      type: "UPDATE_MESSAGGI",
      payload: [...formState.messaggi, newMessage],
    });
    const token = sessionStorage.getItem("token");

    try {
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          message: input.trim(),
          idUIDRequest: idUID,
        }),
      };

      const response = await fetch(
        `${API_BASE_URL}/api/requestSendMessage`,
        requestOptions,
      );
      const res = await response.json();
      console.log(res);
      // Refresh messages after sending
      fetchMessages();
    } catch (e) {
      console.log(e);
    }
    setInput("");
  };

  const scrollToBottom = () => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [formState.messaggi]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;
    setShowScrollBtn(!isAtBottom);
  };

  const groupMessagesByDate = (messages) => {
    const groups = [];
    messages.forEach((msg) => {
      const datePart = msg.dataMessaggio
        ? msg.dataMessaggio.split(" ")[0]
        : "Senza Data";
      let lastGroup = groups[groups.length - 1];

      if (!lastGroup || lastGroup.date !== datePart) {
        groups.push({
          date: datePart,
          messages: [msg],
        });
      } else {
        lastGroup.messages.push(msg);
      }
    });
    return groups;
  };

  const groupedMessages = groupMessagesByDate(formState.messaggi || []);

  const USER_COLORS = [
    "#e542a3", // Pink
    "#35cd96", // Teal
    "#f88a8aff", // Rose
    "#8b3dff", // Purple
    "#00a5f4", // Sky Blue
  ];

  const getUserColor = (identifier) => {
    if (!identifier) return USER_COLORS[0];
    const hash = identifier
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return USER_COLORS[hash % USER_COLORS.length];
  };

  const isDarkMode = theme.palette.mode === "dark";
  const mainBg = isDarkMode ? "#1B2635" : "#efe7de";
  const bubbleOthers = isDarkMode ? "#263548" : "#ffffff";
  const bubbleSelf = isDarkMode ? "#005c4b" : "#e3f2fd";
  const textColor = isDarkMode ? "#ffffffff" : "#111b21";
  const textSecondary = isDarkMode ? "#8696a0" : "#667781";
  const doodleOpacity = isDarkMode ? 0.05 : 0.4;

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: 550,
        display: "flex",
        flexDirection: "column",
        position: "relative",
        bgcolor: mainBg,
        backgroundImage: isDarkMode
          ? "none"
          : "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-8e1d-9bc08c520845.png')",
        overflow: "hidden",
        border: isDarkMode ? "1px solid #2a3942" : "1px solid #d1d7db",
        borderRadius: "0 0 8px 8px",
        "&::before": isDarkMode
          ? {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-8e1d-9bc08c520845.png')",
              opacity: doodleOpacity,
              pointerEvents: "none",
            }
          : {},
      }}
    >
      {/* Chat History */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          p: 2,
          pb: 8,
          zIndex: 1,
        }}
      >
        {groupedMessages.map((group, groupIndex) => (
          <Box
            key={group.date}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            {/* Date Header */}
            <Box
              sx={{
                alignSelf: "center",
                bgcolor: isDarkMode ? "rgba(11, 20, 26, 0.7)" : "#d1f4ff",
                px: 2,
                py: 0.5,
                borderRadius: 2,
                my: 2,
                boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  color: isDarkMode ? "#8696a0" : "#54656f",
                  textTransform: "uppercase",
                }}
              >
                {group.date}
              </Typography>
            </Box>

            {group.messages.map((msg, msgIndex) => {
              const isSelf = msg.idUIDUserId === loggedInUserId;
              const senderName = `${msg.nome} ${msg.cognome}`;
              const timePart = msg.dataMessaggio
                ? msg.dataMessaggio.split(" ")[1]?.substring(0, 5) || ""
                : "";
              const userColor = isSelf
                ? isDarkMode
                  ? "#00a884"
                  : "#1976d2"
                : msg.idUIDUserId === ""
                ? "#ff3c00ff" // Orange for system/Mobility Express (idUIDUserId "")
                : getUserColor(msg.idUIDUserId || senderName);

              return (
                <Box
                  key={msg.id || `${groupIndex}-${msgIndex}`}
                  sx={{
                    display: "flex",
                    flexDirection: isSelf ? "row-reverse" : "row",
                    alignItems: "start",
                    mb: 1,
                    gap: 0.5,
                    alignSelf: isSelf ? "flex-end" : "flex-start",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.7rem",
                      bgcolor: userColor,
                      fontWeight: 600,
                      mb: 0.5, // small lift to align with bubble base
                    }}
                  >
                    {msg.nomeAvatar}
                  </Avatar>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "85%",
                      minWidth: "80px",
                      bgcolor: isSelf ? bubbleSelf : bubbleOthers,
                      borderRadius: isSelf ? "8px 8px 0 8px" : "8px 8px 8px 0",
                      px: 1.5,
                      py: 0.8,
                      boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
                      position: "relative",
                      borderLeft: isSelf ? "none" : `3px solid ${userColor}`,
                    }}
                  >
                    {!isSelf && (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: userColor,
                          mb: 0.2,
                        }}
                      >
                        {senderName}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: textColor,
                          lineHeight: 1.5,
                          mr: 4,
                          fontSize: "0.9rem",
                        }}
                      >
                        {msg.messaggio}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "0.65rem",
                          color: textSecondary,
                          ml: "auto",
                          mt: 0.5,
                          textAlign: "right",
                        }}
                      >
                        {timePart}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {/* Scroll to bottom button */}
      {showScrollBtn && (
        <Fab
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: "absolute",
            bottom: 80,
            right: 20,
            bgcolor: isDarkMode ? "#202c33" : "white",
            color: isDarkMode ? "#aebac1" : "#54656f",
            "&:hover": { bgcolor: isDarkMode ? "#2a3942" : "#f0f2f5" },
            boxShadow:
              "0 1px 1px 0 rgba(11,20,26,.06),0 2px 5px 0 rgba(11,20,26,.2)",
            zIndex: 2,
          }}
        >
          <ArrowDownwardIcon fontSize="small" />
        </Fab>
      )}

      {/* Input Area */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          p: "10px 16px",
          alignItems: "center",
          borderTop: isDarkMode ? "1px solid #3f4e57ff" : "1px solid #d1d7db",
          bgcolor: isDarkMode ? "none" : "#f0f2f5",
          zIndex: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Scrivi un messaggio"
          value={input}
          onChange={(e) => {
            if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
              setInput(e.target.value);
            }
          }}
          onKeyDown={(e) => e.key === "Enter" && handleSend()}
          helperText={`${input.length}/${MAX_MESSAGE_LENGTH}`}
          FormHelperTextProps={{
            sx: {
              textAlign: "right",
              color:
                input.length >= MAX_MESSAGE_LENGTH ? "error.main" : "inherit",
              mt: 1,
              fontSize: "0.65rem",
              fontWeight: 600,
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              bgcolor: isDarkMode ? "#233044" : "white",
              color: isDarkMode ? "#d1d7db" : "black",
              borderRadius: "8px",
              "& fieldset": { border: "none" },
            },
            "& .MuiInputBase-input::placeholder": {
              color: isDarkMode ? "#8696a0" : "inherit",
              opacity: 1,
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim()}
          sx={{
            color: "#407ad6",
            "&.Mui-disabled": { color: isDarkMode ? "#3b4a54" : "#acbac2" },
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}
